import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  getOpsKickoffBriefHtml,
  getOpsProgramEx,
  getOpsProgramLedger,
  TOpsMeetingKind,
} from '../../../requests/opsRegistrationsExtra';
import { getProgramUtilisation } from '../../../requests/opsReports';
import { btnSecondary, PageBody, PageHeader, useListSort } from '../ui';
import AccountingCard from './AccountingCard';
import ContractInfoCard from './ContractInfoCard';
import FieldPanels from './FieldPanels';
import LedgerCard from './LedgerCard';
import MeetingCard from './MeetingCard';
import MembersCard from './MembersCard';
import ProgramCases from './ProgramCases';
import SendCard from './SendCard';
import UtilisationCard from './UtilisationCard';

// The data queries stay here so they all start together when the program
// opens; each card owns its own edit state.
const ProgramDetail: React.FC<{
  programId: string;
  sort: ReturnType<typeof useListSort>;
}> = ({ programId, sort }) => {
  // Each click on Schedule/Reschedule reopens the form from what is saved
  const [meetingForm, setMeetingForm] = useState<{
    kind: TOpsMeetingKind;
    n: number;
  } | null>(null);
  const openMeetingForm = (kind: TOpsMeetingKind) =>
    setMeetingForm((m) => ({ kind, n: (m?.n ?? 0) + 1 }));
  const [briefBusy, setBriefBusy] = useState(false);

  const { data: program, isLoading: loadingDetail } = useQuery({
    queryKey: ['opsProgram', programId],
    queryFn: () => getOpsProgramEx(programId),
    enabled: Boolean(programId),
  });

  const { data: ledger } = useQuery({
    queryKey: ['opsProgramLedger', programId],
    queryFn: () => getOpsProgramLedger(programId),
    enabled: Boolean(programId),
  });

  const { data: utilisation } = useQuery({
    queryKey: ['opsProgramUtilisation', programId],
    queryFn: () => getProgramUtilisation(programId),
    enabled: Boolean(programId),
  });

  // The brief is HTML behind the token, so it is fetched and handed to a
  // new tab rather than linked (a plain link would arrive without auth).
  const openBrief = async () => {
    const w = window.open('', '_blank');
    setBriefBusy(true);
    try {
      const html = await getOpsKickoffBriefHtml(programId);
      if (w) {
        w.document.open();
        w.document.write(html);
        w.document.close();
      }
    } catch (err) {
      w?.close();
      showToast(resolveApiMessage(err), 'error');
    } finally {
      setBriefBusy(false);
    }
  };

  const live = program?.ledgerMode === 'live';

  return (
    <>
      <PageHeader
        title={`Program — ${program?.name ?? '…'}`}
        back={{ to: PATH.opsPrograms, label: 'All programs' }}
        meta={program?.accountName}
        actions={
          program ? (
            <>
              <button
                className={btnSecondary}
                onClick={openBrief}
                disabled={briefBusy}
                title="A printable one-pager for the kickoff call and the instructor's prep call"
              >
                {briefBusy ? 'Building…' : 'Kickoff brief'}
              </button>
              <button
                className={btnSecondary}
                onClick={() => openMeetingForm('kickoff')}
              >
                {program.meetings?.kickoff
                  ? 'Reschedule kickoff'
                  : 'Schedule kickoff'}
              </button>
              <button
                className={btnSecondary}
                onClick={() => openMeetingForm('wrap')}
              >
                {program.meetings?.wrap ? 'Reschedule wrap' : 'Schedule wrap'}
              </button>
            </>
          ) : undefined
        }
      />
      {loadingDetail || !program ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <PageBody>
          {meetingForm && (
            <MeetingCard
              key={`${meetingForm.kind}-${meetingForm.n}`}
              kind={meetingForm.kind}
              programId={programId}
              program={program}
              onClose={() => setMeetingForm(null)}
              openBrief={openBrief}
            />
          )}
          {/* Where the balance comes from — say it before showing it */}
          <div
            className={`mb-4 rounded-md px-4 py-2.5 text-sm ${
              live
                ? 'bg-green-500 text-green-900'
                : 'bg-blue-100 text-blue-primary'
            }`}
          >
            {live
              ? 'Balances are kept by the portal. Registrations, confirmed private events and adjustments move the balance as they happen.'
              : 'Balances are computed by Salesforce until PROGRAM_LEDGER_MODE=live. Portal bookings are rehearsed in the ledger below and do not move the balance yet.'}
          </div>
          {/* Section names and order follow the Salesforce Contract
                layout (docs/familiarity-spec.md) */}
          <div className="mb-4 grid gap-4 lg:grid-cols-2">
            <ContractInfoCard programId={programId} program={program} />
            <AccountingCard program={program} live={live} />
          </div>

          {/* The handoff fields — Program Information, Strategy, Portal
                message — each saved on its own; every write audited */}
          {!program.localOrigin && program.localEditedAt && (
            <div className="mb-4 text-xs text-gray-bold">
              Fields edited here on a Salesforce-born program are kept through
              the nightly refresh; a change to the same field in Salesforce
              shows up on the sync screen to resolve.
            </div>
          )}
          <FieldPanels programId={programId} program={program} />

          <UtilisationCard
            programId={programId}
            program={program}
            utilisation={utilisation}
          />

          {programId && <ProgramCases programId={programId} />}
          <MembersCard programId={programId} program={program} sort={sort} />
          <SendCard programId={programId} program={program} />

          <LedgerCard
            programId={programId}
            program={program}
            ledger={ledger}
            live={live}
          />
        </PageBody>
      )}
    </>
  );
};

export default ProgramDetail;
